import { useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";

import { useUsersTableStore } from "@/stores/useTableStore";

import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import {
  AlertMessageResources,
  ApiResources
} from "../../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../../context/alert/types";
import {
  GeneralDialogProps,
  USERS_ACTIONS,
  useMediaQueries
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import { capitalizeStringOnlyFirstLetter } from "../../../../../../shared/functions/functions";
import useRefreshTableData from "../../../../../../shared/hooks/useRefreshTableData";
import useResponse from "../../../../../../shared/hooks/useResponse";
import Dialog from "../../../../../Dialog/Dialog";

import EditUserDialogContent from "./EditUserDialogContent";

interface IEditUserDialogProps {
  rowData: any;
}

const EditUserDialog = ({
  isOpen,
  onClose,
  rowData
}: IEditUserDialogProps & GeneralDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { putData } = useAxiosPrivate();
  const { handleResponse } = useResponse();
  const { refreshTableData } = useRefreshTableData();

  const params = useUsersTableStore(state => state.params);

  const { contains: valueInSearchField } = params;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [payload, setPayload] = useState<any>(null);

  const resourceString = AlertMessageResources.User;

  const currentRole = {
    id: capitalizeStringOnlyFirstLetter(rowData.role),
    name: capitalizeStringOnlyFirstLetter(t(`General##${rowData.role}`))
  };

  const handleCloseEditUserDialog = () => {
    onClose(USERS_ACTIONS.EditUser);
  };

  const resetAllValues = () => {
    setIsReady(false);
    setPayload(null);
  };

  const handleSubmit = async () => {
    if (isReady) {
      try {
        setIsLoading(true);
        await putData(ApiResources.Users, payload);

        handleResponse(
          ALERT_STATUS.Success,
          t(`Alert##successfully edited`, { resourceString }),
          t("Alert##edit user description")
        );
      } catch (error) {
        const err = error as AxiosError<any>;
        if (err) {
          handleResponse(
            ALERT_STATUS.Critical,
            err.response?.data.message ?? err.message
          );
        }
      } finally {
        setIsLoading(false);
      }
      refreshTableData(undefined, valueInSearchField || undefined);
      handleCloseEditUserDialog();
    }
  };

  return (
    <Dialog
      data-testid={`edit-user-dialog`}
      title={t("Dialog##edit user")}
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={handleCloseEditUserDialog}
      submit={handleSubmit}
      slotProps={{
        transition: { onExited: () => resetAllValues() }
      }}
      actions={
        <DialogActions
          onClose={handleCloseEditUserDialog}
          isLoading={isLoading}
          isreadyToConfirm={isReady}
          confirmationButtonText={t("Button##save")}
          confirmationButtonTestId="save-edit-user-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-edit-user-button"
        />
      }
    >
      <EditUserDialogContent
        rowData={rowData}
        initialRole={currentRole}
        setPayload={setPayload}
        setIsReady={setIsReady}
        isLoading={isLoading}
      />
    </Dialog>
  );
};

export default EditUserDialog;
